import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Video, Camera, Save, Trash2, Loader2, Upload, Film, RefreshCw } from "lucide-react";
import PhotoAnnotator from "@/components/lab/PhotoAnnotator";
import * as api from "@/lib/api";

const fmtT = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;
const uid = () => Math.random().toString(36).slice(2, 10);

function FrameEditor({ vid, frame, onChange, onRemove, idx }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-2" data-testid={`frame-${idx}`}>
      <div className="mb-1.5 flex items-center justify-between text-xs">
        <span className="font-semibold">Fotogramma {fmtT(frame.time)}</span>
        <button type="button" onClick={onRemove} className="rounded p-1 text-muted-foreground hover:text-destructive" data-testid={`frame-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <div className="relative w-full overflow-hidden rounded-lg bg-black">
        <img src={frame.preview || api.portalFrameUrl(vid, frame.id)} alt="" className="block w-full" draggable={false} />
        <PhotoAnnotator value={frame.shapes} onChange={(shapes) => onChange({ ...frame, shapes })} testid={`frame-anno-${idx}`} />
      </div>
      <input className="lab-input mt-2 text-xs" value={frame.caption || ""} onChange={(e) => onChange({ ...frame, caption: e.target.value })} placeholder="Didascalia (opzionale)" data-testid={`frame-caption-${idx}`} />
    </div>
  );
}

function CorrectionEditor({ video, onSaved }) {
  const vref = useRef(null);
  const fileRef = useRef(null);
  const [text, setText] = useState(video.correction?.text || "");
  const [frames, setFrames] = useState(video.correction?.frames || []);
  const [busy, setBusy] = useState(false);
  const [up, setUp] = useState(0);

  const capture = async () => {
    const v = vref.current;
    if (!v || !v.videoWidth) { toast.error("Avvia o posiziona il video sul fotogramma"); return; }
    v.pause();
    const c = document.createElement("canvas");
    const scale = Math.min(1, 1280 / v.videoWidth);
    c.width = Math.round(v.videoWidth * scale); c.height = Math.round(v.videoHeight * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.88));
    setBusy(true);
    try {
      const { image_path } = await api.uploadVideoFrame(video.id, blob);
      setFrames((f) => [...f, { id: uid(), time: v.currentTime, image_path, shapes: [], caption: "", preview: c.toDataURL("image/jpeg", 0.8) }]);
    } catch (e) { toast.error("Cattura non riuscita"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const saved = await api.saveVideoCorrection(video.id, { text, frames: frames.map(({ preview, ...f }) => f) });
      toast.success("Correzione pubblicata");
      onSaved(saved);
    } catch (e) { toast.error("Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  const onCoachVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const f = file.type ? file : new File([file], file.name, { type: "video/mp4" });
      await api.uploadPortalVideo(f, { correction_for: video.id }, setUp);
      toast.success("Video correzione caricato");
      onSaved({ ...video, correction: { ...(video.correction || {}), has_video: true } });
    } catch (err) { toast.error(err?.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setUp(0); }
  };

  return (
    <div className="space-y-4" data-testid="correction-editor">
      <div className="overflow-hidden rounded-xl border border-white/10 bg-black">
        <video ref={vref} src={api.portalFileUrl(video.id, "orig")} controls playsInline preload="metadata" className="max-h-[60vh] w-full" data-testid="admin-video-player" />
      </div>
      <button type="button" onClick={capture} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary disabled:opacity-50" data-testid="capture-frame-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Cattura fotogramma e disegna
      </button>
      {frames.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {frames.map((f, i) => (
            <FrameEditor key={f.id} idx={i} vid={video.id} frame={f} onChange={(nf) => setFrames((all) => all.map((x) => (x.id === f.id ? nf : x)))} onRemove={() => setFrames((all) => all.filter((x) => x.id !== f.id))} />
          ))}
        </div>
      )}
      <div>
        <div className="field-label mb-1">Correzione scritta</div>
        <textarea rows={4} className="lab-input resize-y" value={text} onChange={(e) => setText(e.target.value)} placeholder="Es. Scendi di più, mantieni il busto dritto, ginocchia in linea con le punte…" data-testid="correction-text-input" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={!!up} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-50" data-testid="coach-video-btn">
          {up ? <><Loader2 className="h-4 w-4 animate-spin" /> {up}%</> : <><Upload className="h-4 w-4" /> {video.correction?.has_video ? "Sostituisci tuo video" : "Allega tuo video (opz.)"}</>}
        </button>
        <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={onCoachVideo} data-testid="coach-video-input" />
        {video.correction?.has_video && <span className="inline-flex items-center gap-1 text-xs text-primary"><Film className="h-3.5 w-3.5" /> video coach presente</span>}
        <button type="button" onClick={save} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50" data-testid="publish-correction-btn">
          <Save className="h-4 w-4" /> Pubblica correzione
        </button>
      </div>
    </div>
  );
}

export default function VideoAnalisiTab({ patientId, focusVideoId, onCorrected }) {
  const [videos, setVideos] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try { setVideos(await api.adminPatientVideos(patientId)); } catch (e) { console.error("Video:", e); }
    finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { refresh(); }, [refresh]);
  const focused = useRef(null);
  useEffect(() => {
    if (!focusVideoId || focused.current === focusVideoId) return;
    const v = videos.find((x) => x.id === focusVideoId);
    if (v) { setSel(v); focused.current = focusVideoId; }
  }, [focusVideoId, videos]);

  const remove = async (v) => {
    if (!window.confirm("Eliminare questo video e la sua correzione?")) return;
    try { await api.deletePortalVideo(v.id); if (sel?.id === v.id) setSel(null); refresh(); } catch (e) { toast.error("Eliminazione non riuscita"); }
  };

  if (!patientId) return <div className="panel p-6 text-sm text-muted-foreground" data-testid="tab-video">Salva il paziente per gestire l'analisi video.</div>;
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px_1fr]" data-testid="tab-video">
      <section className="panel p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-base font-bold"><Video className="h-4 w-4 text-primary" /> Video del paziente</h2>
          <button onClick={refresh} title="Aggiorna" className="rounded p-1 text-muted-foreground hover:text-primary" data-testid="videos-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
        {videos.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="videos-empty">Nessun video. Il paziente può caricarli dal suo link personale.</p>
        ) : (
          <div className="space-y-1.5" data-testid="videos-list">
            {videos.map((v) => (
              <div key={v.id} role="button" tabIndex={0} onClick={() => setSel(v)} onKeyDown={(e) => e.key === "Enter" && setSel(v)}
                className={`flex cursor-pointer items-start justify-between gap-2 rounded-lg border px-3 py-2 transition-colors ${sel?.id === v.id ? "border-primary/60 bg-primary/10" : "border-white/10 hover:bg-white/5"}`} data-testid={`video-item-${v.id}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{v.exercise || "Esercizio"}</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(v.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</div>
                  <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[9px] font-bold ${v.correction ? "bg-primary/15 text-primary" : "bg-[hsl(var(--c-fat))]/20 text-[hsl(var(--c-fat))]"}`}>{v.correction ? "CORRETTO" : "DA ANALIZZARE"}</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); remove(v); }} className="rounded p-1 text-muted-foreground hover:text-destructive" data-testid={`video-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="panel p-4 sm:p-5">
        {sel ? (
          <>
            <div className="mb-3">
              <div className="font-display text-lg font-bold">{sel.exercise || "Esercizio"}</div>
              {sel.note && <p className="mt-1 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-xs text-muted-foreground" data-testid="video-patient-note">Nota del paziente: {sel.note}</p>}
            </div>
            <CorrectionEditor key={sel.id} video={sel} onSaved={(v) => { setSel(v); refresh(); onCorrected && onCorrected(); }} />
          </>
        ) : <p className="text-sm text-muted-foreground">Seleziona un video per analizzarlo.</p>}
      </section>
    </div>
  );
}
